import type {
  AnalyzeRequest,
  AnalyzeResponse,
  EstimateRequest,
  EstimateResponse,
} from '@/types/estimation';

const BASE_URL = (import.meta.env.VITE_ESTIMATION_SERVER || 'http://localhost:8000').trim();
const LOCALE = 'fr';

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': LOCALE,
      // Required for ngrok free tier to skip the browser warning interstitial
      'ngrok-skip-browser-warning': 'true',
      ...(options?.headers || {}),
    },
    ...options,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
    let message: string;
    let errorCode: string | undefined;

    if (typeof errorBody.detail === 'string') {
      message = errorBody.detail;
    } else if (Array.isArray(errorBody.detail)) {
      message = errorBody.detail.map((d: { msg?: string }) => d.msg || '').join(', ');
    } else if (
      typeof errorBody.detail === 'object' &&
      errorBody.detail !== null &&
      errorBody.detail.error
    ) {
      // Structured error: { error: "...", reason: { en: "...", fr: "..." } }
      errorCode = errorBody.detail.error;
      const reason = errorBody.detail.reason;
      message =
        (typeof reason === 'object' && reason?.fr) ||
        (typeof reason === 'string' && reason) ||
        errorBody.detail.error;
    } else {
      message = `Erreur serveur (${response.status})`;
    }

    const err = new Error(message);
    (err as any).code = errorCode;
    (err as any).status = response.status;
    throw err;
  }

  return response.json();
}

/** Check if the estimation server is reachable */
export async function healthCheck(): Promise<boolean> {
  try {
    const data = await apiFetch<{ status: string }>(`${BASE_URL}/health`);
    return data.status === 'ok';
  } catch {
    return false;
  }
}

/** Step 1 — Analyze the project description */
export async function analyzeProject(request: AnalyzeRequest): Promise<AnalyzeResponse> {
  return apiFetch<AnalyzeResponse>(`${BASE_URL}/analyze/?lang=${LOCALE}`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/** Step 2 — Submit reviewed selections and get final estimate */
export async function submitEstimate(request: EstimateRequest): Promise<EstimateResponse> {
  return apiFetch<EstimateResponse>(`${BASE_URL}/estimate/?lang=${LOCALE}`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/** Retrieve a previously saved estimate by ID */
export async function getEstimate(estimateId: string): Promise<EstimateResponse> {
  return apiFetch<EstimateResponse>(
    `${BASE_URL}/estimate/${estimateId}?lang=${LOCALE}`
  );
}

/** Upload the generated PDF and user email to the backend */
export async function uploadEstimatePdf(
  estimateId: string,
  email: string,
  pdfBlob: Blob,
): Promise<{ status: string; estimate_id: string; email: string; pdf_size_bytes: number }> {
  const formData = new FormData();
  formData.append('email', email);
  formData.append('pdf', pdfBlob, 'estimation.pdf');

  const response = await fetch(`${BASE_URL}/estimate/${estimateId}/pdf`, {
    method: 'POST',
    body: formData,
    headers: {
      'ngrok-skip-browser-warning': 'true',
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
    throw new Error(typeof err.detail === 'string' ? err.detail : `Erreur serveur (${response.status})`);
  }

  return response.json();
}
